import { InputTags } from '@modules/common/components/InputTags';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { useValidateIntegrationForm } from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  getDiscourseFormFieldsFromURL,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack } from '@mui/material';
import {
  DiscourseIntegrationInputData,
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { SyntheticEvent, useState } from 'react';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

export const DiscourseFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
  integrationID,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const [discourseInputData, setDiscourseInputData] =
    useState<DiscourseIntegrationInputData>();
  const [selectedFormURLs, setSelectedFormURLs] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');

  const { status, createIntegration } = useCreateIntegrationEdgeServer({
    tenantID,
    direction: IntegrationDirection.Inbound,
    channel: IntegrationChannelType.Discourse,
    integrationType,
    createdByUser: userID,
    inputData: {
      discourse: discourseInputData,
    },
    description: descriptionInput.trim(),
    onCompleted: () => {
      onDrawerClose();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.Discourse,
      });
    },
  });

  const handleFormURLChange = (
    _: SyntheticEvent | null,
    formURLs: string[],
  ) => {
    setSelectedFormURLs(formURLs);

    if (errorMessage) {
      setErrorMessage('');
    }

    const selectedDomains: Set<string> = new Set();
    const formCategories: DiscourseIntegrationInputData['subscribedCategories'] =
      [];

    formURLs.forEach((formURL) => {
      const discourseFormFields = getDiscourseFormFieldsFromURL(formURL);

      if (!discourseFormFields) {
        setErrorMessage('Invalid forum URL entered');
        return;
      }

      const { domain, categoryName, categoryID } = discourseFormFields;

      selectedDomains.add(domain);
      formCategories.push({ id: categoryID, name: categoryName });
    });

    // If formURLs domain don't match
    if (selectedDomains.size > 1) {
      setErrorMessage("Entered URL domains don't match");
      return;
    }

    const [domain] = selectedDomains;

    setDiscourseInputData({
      forumURL: domain,
      subscribedCategories: formCategories,
    });
  };

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Discourse,
    integrationType,
    hasValidFormFields:
      !!discourseInputData && !errorMessage && !!selectedFormURLs.length,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      await createIntegration();
      trackIntegrationEvent({
        eventName: 'Integration Form Connect CTA Clicked',
        integrationChannelType: IntegrationChannelType.Discourse,
      });
    },
  });

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
        />
        <Stack p={3} spacing={4}>
          <InputTags
            tags={selectedFormURLs}
            onInput={() => {}}
            onChange={handleFormURLChange}
            label="Discourse Forum URLs"
            placeholder="Enter comma-separated forum URLs"
            {...(!!errorMessage
              ? { error: true, helperText: errorMessage }
              : {})}
            required
          />
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            isValidationInProgress={isValidatingDescription}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText="Integrate"
        isDisabled={isDisabled}
        isLoading={IsLoading(status)}
      />
    </Stack>
  );
};
